/**
 * What the message column should render while the Inbox is being re-scoped.
 *
 * The list used to show its loading state only when it had nothing to show:
 * `isLoading && messages.length === 0`. Every later fetch -- changing mailbox,
 * changing person, a sentiment filter, a page -- kept the previous rows on
 * screen with no signal at all, so switching to another mailbox looked like
 * that mailbox already held the mail you were just reading. On a slow request
 * people clicked a row belonging to the mailbox they had left.
 *
 * Two fetches are deliberately NOT loading states: the SSE-driven refresh and
 * the sentiment backfill poll both run with `{ background: true }`, which
 * never flips `isLoading` -- rows must not flash every time a reply lands.
 */

/** Rows to draw while nothing is known yet, and the ceiling for a redraw. */
export const SKELETON_MAX_ROWS = 6
/** Enough placeholders to read as a list rather than as a stray card. */
export const SKELETON_MIN_ROWS = 3

/**
 * How many placeholder rows to draw.
 *
 * Matching the count the column already had keeps the page from jumping when
 * the new list is a similar size, which is the common case for a mailbox
 * switch. Bounded both ways: a 25-row page must not become 25 skeletons, and
 * a column holding one message must not collapse to one.
 */
export function skeletonRowCount(previousCount: number, pageSize: number): number {
  const ceiling = Math.min(SKELETON_MAX_ROWS, pageSize > 0 ? pageSize : SKELETON_MAX_ROWS)
  const wanted = previousCount > 0 ? previousCount : SKELETON_MAX_ROWS
  return Math.max(Math.min(wanted, ceiling), Math.min(SKELETON_MIN_ROWS, ceiling))
}

export type InboxListView = 'loading' | 'empty' | 'rows'

/**
 * `foregroundFetch` is the store's `isLoading`. `switchingScope` covers the
 * window a mailbox switch spends loading that mailbox's people BEFORE the
 * message request is issued -- nothing was in flight yet, so `isLoading` was
 * still false and the old rows sat there looking settled. It also covers the
 * first paint, when the page has neither a mailbox nor a request and would
 * otherwise flash "No messages yet" at someone whose inbox is full.
 */
export function inboxListView(opts: {
  foregroundFetch: boolean
  switchingScope: boolean
  count: number
}): InboxListView {
  if (opts.foregroundFetch || opts.switchingScope) return 'loading'
  return opts.count === 0 ? 'empty' : 'rows'
}
